/**
 * Diplomacy Panel - UI for diplomatic actions
 */

import { useGameStore } from '@/state/gameStore';
import { FACTIONS } from '@/game/factions/FactionData';
import type { DiplomacySystem, RelationLevel, TreatyType } from '@/game/systems/DiplomacySystem';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { FactionId } from '@/types';

export class DiplomacyPanel {
  private container: HTMLElement;
  private diplomacySystem: DiplomacySystem | null = null;
  private factionManager: FactionManager | null = null;
  private visible: boolean = false;
  private selectedFaction: FactionId | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'diplomacy-panel';
    this.container.innerHTML = `
      <style>
        #diplomacy-panel {
          position: fixed;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: 200;
          font: 13px/1.4 'Noto Serif SC', 'SimSun', serif;
          color: #f5e6d3;
          user-select: none;
          min-width: 420px;
          max-width: 500px;
          display: none;
          pointer-events: auto;
        }
        #diplomacy-panel.visible {
          display: block;
        }
        .diplomacy-panel-wrapper {
          background:
            linear-gradient(145deg, rgba(74,55,40,0.85) 0%, rgba(45,31,21,0.9) 100%),
            url('/images/ui/elements/wood.webp');
          background-size: cover;
          background-position: center;
          border: 3px solid #8b6914;
          border-radius: 8px;
          box-shadow:
            inset 0 2px 4px rgba(255,220,150,0.15),
            inset 0 -3px 6px rgba(0,0,0,0.4),
            0 10px 40px rgba(0,0,0,0.6);
          overflow: hidden;
          position: relative;
        }
        /* Dragon corner ornaments */
        .diplomacy-panel-corner {
          position: absolute;
          width: 40px;
          height: 40px;
          background-image: url('/images/ui/decorations/corner-ornaments.png');
          background-size: 200% 200%;
          z-index: 2;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));
          pointer-events: none;
        }
        .diplomacy-panel-corner.tl { top: -6px; left: -6px; background-position: 0% 0%; }
        .diplomacy-panel-corner.tr { top: -6px; right: -6px; background-position: 100% 0%; }
        .diplomacy-panel-corner.bl { bottom: -6px; left: -6px; background-position: 0% 100%; }
        .diplomacy-panel-corner.br { bottom: -6px; right: -6px; background-position: 100% 100%; }
        .diplomacy-panel-title-bar {
          background: linear-gradient(180deg, #6b4423 0%, #4a2f17 100%);
          border-bottom: 2px solid #8b6914;
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .diplomacy-panel-title {
          font: 700 16px/1 'Noto Serif SC', 'SimSun', serif;
          color: #ffd35a;
          text-shadow: 0 2px 4px rgba(0,0,0,0.5);
        }
        #diplomacy-panel .close-btn {
          background: none;
          border: none;
          color: #c4a77d;
          cursor: pointer;
          font-size: 20px;
          padding: 0 4px;
          transition: color 0.2s;
        }
        #diplomacy-panel .close-btn:hover {
          color: #ffd35a;
        }
        .diplomacy-panel-content {
          padding: 12px;
          max-height: 60vh;
          overflow-y: auto;
        }
        #diplomacy-panel .faction-row {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          margin: 4px 0;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 2px solid #6b4423;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        #diplomacy-panel .faction-row:hover {
          border-color: #8b6914;
          transform: translateX(2px);
        }
        #diplomacy-panel .faction-row.selected {
          border-color: #d4af37;
          background: linear-gradient(180deg, #6b4e3a 0%, #4a3525 100%);
          box-shadow: 0 0 10px rgba(212,175,55,0.3);
        }
        #diplomacy-panel .faction-color {
          width: 20px;
          height: 20px;
          border-radius: 4px;
          margin-right: 12px;
          border: 2px solid rgba(0,0,0,0.3);
        }
        #diplomacy-panel .faction-info {
          flex: 1;
        }
        #diplomacy-panel .faction-name {
          font-weight: 600;
          color: #f5e6d3;
        }
        #diplomacy-panel .relation-badge {
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          border: 1px solid rgba(0,0,0,0.3);
        }
        #diplomacy-panel .relation-war { background: linear-gradient(180deg, #8b2020 0%, #5c1515 100%); color: #ffcdd2; }
        #diplomacy-panel .relation-hostile { background: linear-gradient(180deg, #bf5600 0%, #8b4000 100%); color: #ffe0b2; }
        #diplomacy-panel .relation-neutral { background: linear-gradient(180deg, #5c5c5c 0%, #3d3d3d 100%); color: #e0e0e0; }
        #diplomacy-panel .relation-friendly { background: linear-gradient(180deg, #2e7d32 0%, #1b5e20 100%); color: #c8e6c9; }
        #diplomacy-panel .relation-alliance { background: linear-gradient(180deg, #1565c0 0%, #0d47a1 100%); color: #bbdefb; }

        #diplomacy-panel .actions-section {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 2px solid rgba(139,105,20,0.4);
        }
        #diplomacy-panel .actions-title {
          font-weight: 600;
          margin-bottom: 10px;
          color: #c4a77d;
        }
        #diplomacy-panel .action-btn {
          display: block;
          width: 100%;
          padding: 10px 14px;
          margin: 6px 0;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 2px solid #6b4423;
          border-radius: 6px;
          color: #f5e6d3;
          cursor: pointer;
          font: 13px 'Noto Serif SC', 'SimSun', serif;
          text-align: left;
          transition: all 0.15s ease;
        }
        #diplomacy-panel .action-btn:hover:not(:disabled) {
          border-color: #8b6914;
          background: linear-gradient(180deg, #6b4e3a 0%, #4a3525 100%);
        }
        #diplomacy-panel .action-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        #diplomacy-panel .action-btn.danger {
          background: linear-gradient(180deg, #6b2020 0%, #4a1515 100%);
          border-color: #8b3030;
        }
        #diplomacy-panel .action-btn.danger:hover:not(:disabled) {
          background: linear-gradient(180deg, #8b2020 0%, #5c1515 100%);
          border-color: #c73e3a;
        }
        #diplomacy-panel .treaties-list {
          margin-top: 10px;
          padding: 10px;
          background: rgba(0,0,0,0.2);
          border: 1px solid rgba(139,105,20,0.3);
          border-radius: 6px;
        }
        #diplomacy-panel .treaty-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 0;
          font-size: 12px;
          color: #c4a77d;
        }
      </style>
      <div class="diplomacy-panel-wrapper">
        <div class="diplomacy-panel-corner tl"></div>
        <div class="diplomacy-panel-corner tr"></div>
        <div class="diplomacy-panel-corner bl"></div>
        <div class="diplomacy-panel-corner br"></div>
        <div class="diplomacy-panel-title-bar">
          <span class="diplomacy-panel-title">外交</span>
          <button class="close-btn">&times;</button>
        </div>
        <div class="diplomacy-panel-content">
          <div class="faction-list"></div>
          <div class="actions-section" style="display: none;">
            <div class="actions-title">外交行动</div>
            <div class="actions-content"></div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.container);
    this.setupEvents();
  }

  setDiplomacySystem(system: DiplomacySystem): void {
    this.diplomacySystem = system;
  }

  setFactionManager(manager: FactionManager): void {
    this.factionManager = manager;
  }

  private setupEvents(): void {
    const closeBtn = this.container.querySelector('.close-btn');
    closeBtn?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.hide();
    });

    // Close on escape
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.visible) {
        this.hide();
      }
    });
  }

  show(): void {
    this.visible = true;
    this.container.classList.add('visible');
    this.render();
  }

  hide(): void {
    this.visible = false;
    this.container.classList.remove('visible');
    this.selectedFaction = null;
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  private render(): void {
    if (!this.diplomacySystem || !this.factionManager) return;

    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    const listEl = this.container.querySelector('.faction-list');
    if (!listEl) return;

    const factionIds: FactionId[] = ['yan', 'han', 'lin', 'shang', 'xuan', 'tie', 'yun'];
    const otherFactions = factionIds.filter(f => f !== playerFaction);

    listEl.innerHTML = otherFactions.map(factionId => {
      const faction = FACTIONS[factionId];
      const state = this.factionManager!.getFactionState(factionId);
      if (!state || state.isDefeated) return '';

      const colorHex = '#' + faction.color.toString(16).padStart(6, '0');
      const relationLevel = this.diplomacySystem!.getRelationLevel(playerFaction, factionId);
      const relationValue = this.factionManager!.getRelation(playerFaction, factionId);
      const isSelected = this.selectedFaction === factionId;

      return `
        <div class="faction-row ${isSelected ? 'selected' : ''}" data-faction="${factionId}">
          <div class="faction-color" style="background: ${colorHex}"></div>
          <div class="faction-info">
            <div class="faction-name">${faction.name}</div>
            <div style="font-size: 11px; opacity: 0.7;">关系值: ${relationValue}</div>
          </div>
          <span class="relation-badge relation-${relationLevel}">${this.getRelationLabel(relationLevel)}</span>
        </div>
      `;
    }).join('');

    // Add click handlers - use pointerdown to avoid game's pointer capture
    const rows = listEl.querySelectorAll('.faction-row');
    rows.forEach(row => {
      row.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        const factionId = (row as HTMLElement).dataset.faction as FactionId;
        this.selectedFaction = factionId;
        this.render();
      });
    });

    // Render actions for selected faction
    this.renderActions();
  }

  private renderActions(): void {
    const actionsSection = this.container.querySelector('.actions-section') as HTMLElement;
    const actionsContent = this.container.querySelector('.actions-content');
    if (!actionsSection || !actionsContent) return;

    if (!this.selectedFaction || !this.diplomacySystem || !this.factionManager) {
      actionsSection.style.display = 'none';
      return;
    }

    actionsSection.style.display = 'block';
    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    const relationLevel = this.diplomacySystem.getRelationLevel(playerFaction, this.selectedFaction);
    const faction = FACTIONS[this.selectedFaction];
    const hasNonAggression = this.diplomacySystem.hasActiveTreaty(playerFaction, this.selectedFaction, 'non_aggression');
    const hasTrade = this.diplomacySystem.hasActiveTreaty(playerFaction, this.selectedFaction, 'trade');
    const hasAlliance = this.diplomacySystem.hasActiveTreaty(playerFaction, this.selectedFaction, 'alliance');

    let actionsHtml = `<div style="margin-bottom: 10px;">与 <strong>${faction.name}</strong> 的外交行动:</div>`;

    if (relationLevel === 'war') {
      actionsHtml += `
        <button class="action-btn" data-action="propose-peace">
          🕊️ 提议和平
        </button>
      `;
    } else {
      // Non-aggression pact
      if (!hasNonAggression && !hasAlliance) {
        actionsHtml += `
          <button class="action-btn" data-action="propose-non-aggression">
            🤝 签订互不侵犯条约 (3分钟)
          </button>
        `;
      }

      // Trade agreement
      if (!hasTrade && relationLevel !== 'hostile') {
        actionsHtml += `
          <button class="action-btn" data-action="propose-trade">
            💰 签订贸易协定 (+10% 收入)
          </button>
        `;
      }

      // Alliance
      if (!hasAlliance && (relationLevel === 'friendly' || relationLevel === 'alliance')) {
        actionsHtml += `
          <button class="action-btn" data-action="propose-alliance">
            ⚔️ 缔结军事同盟 (共享视野)
          </button>
        `;
      }

      // Declare war
      if (!hasNonAggression && !hasAlliance) {
        actionsHtml += `
          <button class="action-btn danger" data-action="declare-war">
            ⚔️ 宣战
          </button>
        `;
      }
    }

    // Show active treaties
    const treaties = this.diplomacySystem.getTreatiesFor(playerFaction)
      .filter(t => t.parties.includes(this.selectedFaction!));

    if (treaties.length > 0) {
      actionsHtml += `<div class="treaties-list">`;
      actionsHtml += `<div style="font-weight: 600; margin-bottom: 6px;">现有条约:</div>`;
      for (const treaty of treaties) {
        actionsHtml += `
          <div class="treaty-item">
            <span>${this.getTreatyLabel(treaty.type)}</span>
            <button class="action-btn" style="width: auto; padding: 2px 8px; margin: 0;"
                    data-action="break-treaty" data-treaty-id="${treaty.id}">
              解除
            </button>
          </div>
        `;
      }
      actionsHtml += `</div>`;
    }

    actionsContent.innerHTML = actionsHtml;

    // Add action handlers - use pointerdown to avoid game's pointer capture
    const actionBtns = actionsContent.querySelectorAll('.action-btn');
    actionBtns.forEach(btn => {
      btn.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        const action = (btn as HTMLElement).dataset.action;
        this.handleAction(action!, btn as HTMLElement);
      });
    });
  }

  private handleAction(action: string, btn: HTMLElement): void {
    if (!this.selectedFaction || !this.diplomacySystem) return;

    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    switch (action) {
      case 'propose-peace':
        this.diplomacySystem.proposePeace(playerFaction, this.selectedFaction);
        useGameStore.getState().addMessage(`向 ${FACTIONS[this.selectedFaction].name} 提议和平`);
        break;

      case 'propose-non-aggression':
        // Directly create treaty (AI auto-accepts for simplicity)
        if (this.diplomacySystem.createTreaty(playerFaction, this.selectedFaction, 'non_aggression')) {
          useGameStore.getState().addMessage(`与 ${FACTIONS[this.selectedFaction].name} 签订了互不侵犯条约`);
        } else {
          useGameStore.getState().addMessage(`无法与 ${FACTIONS[this.selectedFaction].name} 签订条约`);
        }
        break;

      case 'propose-trade':
        if (this.diplomacySystem.createTreaty(playerFaction, this.selectedFaction, 'trade')) {
          useGameStore.getState().addMessage(`与 ${FACTIONS[this.selectedFaction].name} 签订了贸易协定`);
        } else {
          useGameStore.getState().addMessage(`无法与 ${FACTIONS[this.selectedFaction].name} 签订贸易协定`);
        }
        break;

      case 'propose-alliance':
        if (this.diplomacySystem.createTreaty(playerFaction, this.selectedFaction, 'alliance')) {
          useGameStore.getState().addMessage(`与 ${FACTIONS[this.selectedFaction].name} 结成了军事同盟`);
        } else {
          useGameStore.getState().addMessage(`无法与 ${FACTIONS[this.selectedFaction].name} 结盟`);
        }
        break;

      case 'declare-war':
        if (this.diplomacySystem.declareWar(playerFaction, this.selectedFaction)) {
          useGameStore.getState().addMessage(`向 ${FACTIONS[this.selectedFaction].name} 宣战!`);
        }
        break;

      case 'break-treaty':
        const treatyId = parseInt(btn.dataset.treatyId || '0');
        if (treatyId && this.diplomacySystem.breakTreaty(treatyId, playerFaction)) {
          useGameStore.getState().addMessage(`解除了与 ${FACTIONS[this.selectedFaction].name} 的条约`);
        }
        break;
    }

    this.render();
  }

  private getRelationLabel(level: RelationLevel): string {
    const labels: Record<RelationLevel, string> = {
      war: '战争',
      hostile: '敌对',
      neutral: '中立',
      friendly: '友好',
      alliance: '同盟',
    };
    return labels[level];
  }

  private getTreatyLabel(type: string): string {
    const labels: Record<string, string> = {
      non_aggression: '互不侵犯条约',
      trade: '贸易协定',
      alliance: '军事同盟',
    };
    return labels[type] || type;
  }

  dispose(): void {
    this.container.remove();
  }
}
